import { useEffect, useMemo, useState } from 'react';
import { X, Upload, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { DOC_ACCEPT, docContentType, errText, uploadDoc, type Catalog, type CatalogDoc, type Doc, type Surface } from '@/lib/documents';

/** "English | Arabic" in one option, so both read in the closed field and the menu. */
const bi = (en?: string, ar?: string) => (ar ? `${en ?? ''}  |  ${ar}` : en ?? '');

export interface Preset { catalogKey?: string | null; docType?: string; authority?: string | null }

/** Upload one document: category, then authority or platform, then the exact document (each
 *  step filters the next), then its details and the file. Used by staff and by the clinic. */
export default function UploadDialog({ surface, catalog, open, onClose, onDone, preset, replace, clinicUpload = false }: {
  surface: Surface; catalog: Catalog; open: boolean; onClose: () => void; onDone: (d: Doc) => void;
  preset?: Preset | null; replace?: Doc | null; clinicUpload?: boolean;
}) {
  // a clinic never uploads KAF's own papers (contract, NDA)
  const categories = useMemo(() => catalog.categories.map((c) => ({
    ...c,
    authorities: c.authorities.map((a) => ({
      ...a,
      documents: a.documents.filter((d) => !(clinicUpload && catalog.kaf_types.includes(d.doc_type))
        && (!replace || d.doc_type === replace.doc_type)),
    })).filter((a) => a.documents.length),
  })).filter((c) => c.authorities.length), [catalog, clinicUpload, replace]);

  const [cat, setCat] = useState('');
  const [auth, setAuth] = useState('');
  const [key, setKey] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [number, setNumber] = useState('');
  const [issue, setIssue] = useState('');
  const [expiry, setExpiry] = useState('');
  const [emirate, setEmirate] = useState('');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [pct, setPct] = useState<number | null>(null);

  // start from the checklist's suggestion, or from the document being replaced
  useEffect(() => {
    if (!open) return;
    const want = replace?.catalog_key || preset?.catalogKey || '';
    let c = '', a = '', k = '';
    for (const cc of categories) for (const aa of cc.authorities) for (const d of aa.documents) {
      if (!k && want && d.key === want) { c = cc.key; a = aa.key; k = d.key; }
    }
    if (!k && (preset?.docType || replace)) {
      const t = replace?.doc_type || preset?.docType;
      const au = replace?.authority || preset?.authority;
      for (const cc of categories) for (const aa of cc.authorities) if (!c && aa.documents.some((d) => d.doc_type === t) && (!au || aa.key === au)) {
        c = cc.key; a = aa.key;
        const only = aa.documents.filter((d) => d.doc_type === t);
        if (only.length === 1) k = only[0].key;
      }
    }
    setCat(c); setAuth(a); setKey(k);
    setFile(null); setNumber(replace?.number ?? ''); setIssue(''); setExpiry(''); setEmirate(replace?.emirate ?? '');
    setTitle(''); setNotes(''); setPct(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const category = categories.find((c) => c.key === cat);
  const authority = category?.authorities.find((a) => a.key === auth);
  const entry: CatalogDoc | undefined = authority?.documents.find((d) => d.key === key);
  const has = (f: string) => !!entry?.fields.includes(f);

  if (!open) return null;
  const tooBig = !!file && file.size > catalog.max_bytes;
  const badType = !!file && !catalog.content_types.includes(docContentType(file));
  const ready = !!entry && !!file && !tooBig && !badType && pct === null;

  const submit = async () => {
    if (!entry || !file) return;
    setPct(0);
    try {
      const d = await uploadDoc(surface, file, {
        doc_type: entry.doc_type, catalog_key: entry.key, authority: auth,
        title: title.trim() || null, number: number.trim() || null, issue_date: issue || null,
        expiry_date: expiry || null, emirate: emirate || null, notes: notes.trim() || null, replaces: replace?.id ?? null,
      }, setPct);
      toast.success(replace ? 'New version uploaded. Checking the document...' : 'Uploaded. Checking the document...');
      onDone(d);
    } catch (e) {
      toast.error(errText(e, 'Upload failed'));
      setPct(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60" role="dialog" aria-modal="true" data-testid="doc-upload-dialog">
      <div className="w-full sm:max-w-2xl max-h-[92vh] min-h-[70vh] sm:min-h-[620px] overflow-y-auto bg-bg-card border border-slate-800 rounded-t-2xl sm:rounded-2xl p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">{replace ? `Replace: ${replace.title}` : 'Upload a document'}</h2>
            <p className="text-xs text-muted" dir="rtl">{replace ? 'استبدال المستند بنسخة أحدث' : 'رفع مستند'}</p>
          </div>
          <button onClick={onClose} className="text-muted hover:text-white" aria-label="Close"><X size={18} /></button>
        </div>

        <div className="grid gap-3 relative z-10">
          <Select label="1. Document category  |  فئة المستند" value={cat} data-testid="doc-step-category" menuClassName="z-50"
            onChange={(v) => { setCat(v); setAuth(''); setKey(''); }} placeholder="Choose a category  |  اختر الفئة"
            options={categories.map((c) => ({ value: c.key, label: bi(c.label_en, c.label_ar) }))} />
          <Select label="2. Authority or platform  |  الجهة أو المنصة" value={auth} disabled={!category} searchable data-testid="doc-step-authority" menuClassName="z-50"
            onChange={(v) => { setAuth(v); const a = category?.authorities.find((x) => x.key === v); setKey(a && a.documents.length === 1 ? a.documents[0].key : ''); }}
            placeholder="Choose who issues it  |  اختر الجهة المصدرة"
            options={(category?.authorities ?? []).map((a) => ({ value: a.key, label: bi(a.label_en, a.label_ar) }))} />
          <Select label="3. Exact document  |  المستند" value={key} disabled={!authority} data-testid="doc-step-document" menuClassName="z-50"
            onChange={setKey} placeholder="Choose the document  |  اختر المستند"
            options={(authority?.documents ?? []).map((d) => ({ value: d.key, label: bi(d.name_en, d.name_ar), hint: d.verified ? undefined : 'unverified' }))} />
        </div>

        {entry && (
          <div className="mt-4 rounded-lg border border-slate-800 bg-bg/60 p-3 text-sm grid gap-2" data-testid="doc-entry-info">
            <div className="grid sm:grid-cols-2 gap-2">
              <div>
                <div className="text-xs text-muted">Issued by</div>
                <div className="text-muted-strong">{entry.issuing_authority_en}</div>
                <div className="text-muted-strong" dir="rtl">{entry.issuing_authority_ar}</div>
              </div>
              <div>
                <div className="text-xs text-muted">What it proves</div>
                <div className="text-muted-strong">{entry.proves_en}</div>
                <div className="text-muted-strong" dir="rtl">{entry.proves_ar}</div>
              </div>
            </div>
            <div className="text-xs text-muted">
              {entry.expires ? `Expires${entry.validity_note_en ? `: ${entry.validity_note_en}` : ''}` : 'Does not expire'}
              {entry.source_url && entry.source_url !== 'internal' && (
                <> - <a href={entry.source_url} target="_blank" rel="noreferrer" className="text-accent-400 inline-flex items-center gap-0.5">Source, checked {entry.checked_date} <ExternalLink size={10} /></a></>
              )}
            </div>
            {entry.note_en && (
              <div className={entry.verified ? 'text-xs text-muted' : 'text-xs text-amber-300 flex gap-1'}>
                {!entry.verified && <AlertTriangle size={12} className="shrink-0 mt-0.5" />}{entry.verified ? entry.note_en : (entry.note_en.startsWith('Unverified') ? entry.note_en : `Unverified: ${entry.note_en}`)}
              </div>
            )}
          </div>
        )}

        {entry && (
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {(has('number') || !entry.fields.length) && (
              <Input label={`${entry.number_label_en || 'Number'}  |  ${entry.number_label_ar || 'الرقم'}`} value={number}
                onChange={(e) => setNumber(e.target.value)} data-testid="doc-number" />
            )}
            {has('emirate') && (
              <Select label="Emirate  |  الإمارة" value={emirate} onChange={setEmirate} placeholder="Choose  |  اختر"
                options={Object.entries(catalog.emirates).map(([k, v]) => ({ value: k, label: bi(v.en, v.ar) }))} />
            )}
            {has('issue_date') && <Input type="date" label="Issue date  |  تاريخ الإصدار" value={issue} onChange={(e) => setIssue(e.target.value)} />}
            {(has('expiry_date') || entry.expires) && (
              <Input type="date" label="Expiry date  |  تاريخ الانتهاء" value={expiry} onChange={(e) => setExpiry(e.target.value)}
                hint="Left empty, it is read from the document" data-testid="doc-expiry" />
            )}
            <Input label="Title (optional)  |  العنوان" value={title} placeholder={entry.name_en} onChange={(e) => setTitle(e.target.value)} />
            <label className="flex flex-col gap-1.5 text-sm sm:col-span-2">
              <span className="text-xs text-muted">Notes  |  ملاحظات</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                className="rounded-lg border border-slate-800 bg-bg px-3 py-2 text-sm text-muted-strong focus:outline-none focus:border-accent-400" />
            </label>
            <label className="flex flex-col gap-1.5 text-sm sm:col-span-2">
              <span className="text-xs text-muted">File: PDF, JPG, PNG, WEBP or Word, up to 25 MB  |  الملف</span>
              <input type="file" accept={DOC_ACCEPT} onChange={(e) => setFile(e.target.files?.[0] ?? null)} data-testid="doc-file"
                className="text-sm text-muted-strong file:mr-3 file:rounded-md file:border-0 file:bg-slate-800 file:px-3 file:py-2 file:text-muted-strong" />
              {tooBig && <span className="text-xs text-red-400">This file is over 25 MB.</span>}
              {badType && <span className="text-xs text-red-400">PDF, JPG, PNG, WEBP or Word (DOCX) only.</span>}
            </label>
          </div>
        )}

        <div className="mt-5 relative z-0 flex items-center justify-end gap-2">
          {pct !== null && <span className="text-xs text-muted mr-auto">Uploading {pct}%</span>}
          <Button variant="ghost" onClick={onClose} disabled={pct !== null}>Cancel</Button>
          <Button icon={<Upload size={14} />} disabled={!ready} loading={pct !== null} onClick={submit} data-testid="doc-submit">
            {replace ? 'Upload new version' : 'Upload'}
          </Button>
        </div>
      </div>
    </div>
  );
}
